import React from 'react';
import styled from 'styled-components';
import { FiCheckCircle, FiX, FiAlertCircle, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const PopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupContainer = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: ${props => props.theme.shadows.large};
  position: relative;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const PopupTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.colors.text.primary};
  font-size: 18px;
  font-weight: 600;
`;

const PopupMessage = styled.p`
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.5;
`;

const PopupButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const PopupButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  
  &.primary {
    background: ${props => props.theme.colors.primary};
    color: white;
    
    &:hover {
      opacity: 0.9;
    }
  }
  
  &.secondary {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text.primary};
    border: 1px solid ${props => props.theme.colors.border};
    
    &:hover {
      background: ${props => props.theme.colors.surface};
    }
  }
`;

const Popup = ({ isOpen, onClose, type = 'info', title, message, showCancel = false, onConfirm }) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FiCheckCircle size={24} color="#4CAF50" />;
      case 'error':
        return <FiAlertCircle size={24} color="#f44336" />;
      case 'warning':
        return <FiAlertTriangle size={24} color="#ff9800" />;
      case 'info':
        return <FiInfo size={24} color="#2196F3" />;
      default:
        return <FiInfo size={24} color="#2196F3" />;
    } 
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <PopupOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
        >
          <PopupContainer
            initial={{ scale: 0.8, opacity: 0, y: -50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: -50 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <PopupHeader>
              {getIcon()}
              <PopupTitle>{title}</PopupTitle>
            </PopupHeader>
            <PopupMessage>{message}</PopupMessage>
            <PopupButtons>
              {showCancel && (
                <PopupButton className="secondary" onClick={onClose}>
                  Cancel
                </PopupButton>
              )}
              <PopupButton 
                className="primary" 
                onClick={onConfirm || onClose}
              >
                OK
              </PopupButton>
            </PopupButtons>
          </PopupContainer>
        </PopupOverlay>
      )}
    </AnimatePresence>
  );
};

export default Popup;